// src/features/weekly-one/WeeklyOneSetModal.tsx
import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useWeeklyOne } from '@/features/weekly-one/WeeklyOneContext';
import { getCTWeekEnd } from '@/features/weekly-one/weeklyOneUtils';

const COMMITMENT_EXAMPLES = [
  'Call John the Realtor and ask about 123 Main Street',
  'Book my first mentor call',
  'Drive for Dollars for 1 hour in the Eastside neighborhoods',
];

type LastWeekOutcome = 'completed' | 'partial' | 'missed' | '';

const OUTCOME_OPTIONS: { value: Exclude<LastWeekOutcome, ''>; label: string }[] = [
  { value: 'completed',  label: 'Completed' },
  { value: 'partial',    label: 'Partially' },
  { value: 'missed',     label: "Didn't get to it" },
];

function formatWeekEnd(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export default function WeeklyOneSetModal() {
  const {
    setModalOpen,
    closeSetModal,
    currentCommitment,
    saveCommitment,
    editCommitment,
    lastWeekCommitment,
  } = useWeeklyOne();

  const isEdit = !!currentCommitment && currentCommitment.status === 'active';
  const showLastWeek = !isEdit && lastWeekCommitment !== null && lastWeekCommitment?.status === 'active';

  const [commitment, setCommitment] = useState('');
  const [blocker, setBlocker] = useState('');
  const [lastWeekOutcome, setLastWeekOutcome] = useState<LastWeekOutcome>('');
  const [saving, setSaving] = useState(false);

  const [exampleIdx] = useState(() => Math.floor(Math.random() * COMMITMENT_EXAMPLES.length));

  useEffect(() => {
    if (setModalOpen) {
      setCommitment(isEdit ? (currentCommitment?.commitment_text ?? '') : '');
      setBlocker(isEdit ? (currentCommitment?.blocker_text ?? '') : '');
      setLastWeekOutcome('');
    }
  }, [setModalOpen, isEdit, currentCommitment]);

  const handleSave = async () => {
    if (!commitment.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await editCommitment(commitment.trim(), 0, blocker);
      } else {
        await saveCommitment(
          commitment.trim(),
          0,
          blocker,
          lastWeekOutcome || undefined,
        );
      }
      closeSetModal();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const weekEndLabel = formatWeekEnd(getCTWeekEnd());

  return (
    <Dialog open={setModalOpen} onOpenChange={(open) => { if (!open) closeSetModal(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 bg-[#F7F3EE]">
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs font-semibold tracking-widest uppercase text-amber-700">
              My Weekly One
            </p>
            <button
              onClick={closeSetModal}
              className="text-muted-foreground hover:text-foreground transition-colors -mt-0.5 -mr-1 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h2 className="font-serif text-2xl font-bold text-foreground leading-snug">
            Your{' '}
            <em className="text-amber-700 not-italic font-serif italic">one thing</em>{' '}
            this week
          </h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            If you do nothing else, what&apos;s the one move that gets you closer to your first deal? Be specific.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Commitment */}
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">Your commitment</p>
            <div className="relative">
              <Textarea
                placeholder={`e.g., ${COMMITMENT_EXAMPLES[exampleIdx]}`}
                value={commitment}
                onChange={e => setCommitment(e.target.value.slice(0, 200))}
                className="resize-none min-h-[90px] pr-3 pb-6"
              />
              <span className="absolute bottom-2 right-3 text-[11px] text-muted-foreground">
                {commitment.length}/200
              </span>
            </div>
          </div>

          {/* Biggest blocker */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-semibold text-foreground">
                What's your biggest blocker right now?
              </p>
              <span className="text-[10px] font-medium text-muted-foreground border border-border rounded-full px-1.5 py-0.5 leading-none">
                Optional
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Helps us know how to support you better.
            </p>
            <Textarea
              value={blocker}
              onChange={e => setBlocker(e.target.value.slice(0, 1000))}
              placeholder="e.g., I'm not sure how to evaluate whether a deal actually cash flows"
              className="resize-none min-h-[72px]"
            />
          </div>

          {/* Last week's commitment (conditional) */}
          {showLastWeek && (
            <>
              <div className="border-t border-border" />
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">
                  Last Week&apos;s Commitment
                </p>
                <p className="text-sm text-muted-foreground italic mb-3 leading-relaxed">
                  &ldquo;{lastWeekCommitment!.commitment_text}&rdquo;
                </p>
                <div className="flex gap-2 flex-wrap">
                  {OUTCOME_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setLastWeekOutcome(prev => prev === value ? '' : value)}
                      className={`flex-1 min-w-[90px] py-2 rounded-lg border text-sm font-medium transition-all ${
                        lastWeekOutcome === value
                          ? 'border-amber-500/60 bg-amber-50/30 text-foreground'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <Button
            className="w-full gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 transition-opacity h-12 text-base"
            disabled={saving || !commitment.trim()}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Commit Your Weekly One'}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-2">
            Due {weekEndLabel}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
